import type { Work } from "./api";

export type WorkAttentionTarget = "source" | "calculation" | "details" | "norms" | "history";

export type WorkAttentionItem = {
  key: string;
  topic: "quantity" | "norm" | "basis" | "note";
  origin: "current" | "note";
  title: string;
  evidence: string[];
  target: WorkAttentionTarget;
  action: string;
  actionLabel: string;
};

export type WorkAttentionPresentation = {
  current: WorkAttentionItem[];
  notes: WorkAttentionItem[];
  excluded: boolean;
};

/** Current fields and unstructured analysis notes deliberately remain separate. */
export function workAttention(work: Work): WorkAttentionPresentation {
  const current: WorkAttentionItem[] = [];

  if (work.quantity == null || work.quantity.trim() === "") {
    current.push({
      key: "quantity",
      topic: "quantity",
      origin: "current",
      title: "Объём не установлен",
      evidence: ["Поле объёма работы не заполнено. Объём нужно установить по исходным данным."],
      target: "calculation",
      action: "Уточнить объём",
      actionLabel: "Открыть расчёт",
    });
  }

  if (work.norms.length === 0) {
    current.push({
      key: "norm-missing",
      topic: "norm",
      origin: "current",
      title: "Норма не назначена",
      evidence: ["У работы нет сохранённых применений норм. Это не означает, что подходящие варианты не найдены."],
      target: "norms",
      action: "Подобрать норму",
      actionLabel: "Открыть нормы",
    });
  }

  work.norms.forEach((application, index) => {
    if (!application.error?.trim()) return;
    const norm = application.norm.code.trim() || application.norm.id;
    current.push({
      key: `norm-error:${application.id}:${index}`,
      topic: "norm",
      origin: "current",
      title: `Применение нормы ${norm} требует проверки`,
      evidence: [application.error],
      target: "norms",
      action: "Проверить применение нормы",
      actionLabel: "Открыть нормы",
    });
  });

  if (work.source_ids.length === 0) {
    current.push({
      key: "source-missing",
      topic: "basis",
      origin: "current",
      title: "Источник работы не указан",
      evidence: ["К работе не привязан источник. Формула и ссылки на измерения не заменяют привязку исходного документа."],
      target: "source",
      action: "Проверить основание",
      actionLabel: "Открыть источник",
    });
  }

  return {
    current,
    notes: work.issues.map((note, index) => ({
      key: `note:${index}`,
      topic: "note",
      origin: "note",
      title: "Примечание из анализа",
      evidence: [note],
      target: "source",
      action: "Сверить примечание с текущими данными",
      actionLabel: "Открыть источник",
    })),
    excluded: work.status === "rejected",
  };
}
